import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { clsx } from 'clsx';
import { IconButton } from '@design-system/components/button/iconButton';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import _ from 'lodash';
import { sprinkles } from '@design-system/sprinkles.css';
import { ClipboardPanelItem } from './ClipboardPanelItem';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { WorkoutItem } from '../../models/workout.domain';

export const ClipboardPanel = ({
  close,
  data,
  deleteItem,
  clearAll,
  paste,
  disabledPaste,
}: {
  close: () => void;
  data: WorkoutItem[];
  deleteItem: (index: number) => void;
  clearAll: () => void;
  paste: (data: WorkoutItem[]) => void;
  disabledPaste?: boolean;
}) => {
  const [selection, setSelection] = useState<number[]>([]);

  const removeSelection = (index: number) => {
    setSelection((prev) => [...prev, index]);
  };

  const addSelection = (index: number) => {
    setSelection((prev) => prev.filter((p) => p !== index));
  };

  const pasteSelection = () => {
    if (_.isEmpty(selection)) return;

    const pasteData = data.filter((d, i) => selection.includes(i));
    paste(pasteData);
    setSelection([]);
    toast.success(
      `${pasteData.length > 1 ? 'Sections' : 'Section'} added successfully`,
    );
  };

  const pasteAll = () => {
    paste(data);
    toast.success(
      `${data.length > 1 ? 'Sections' : 'Section'} added successfully`,
    );
  };

  const clear = () => {
    clearAll();
    close();
  };

  return (
    <div
      className={clsx(
        'z-1 absolute bottom-[80px] right-[-10px] bg-white rounded-md w-[450px] flex flex-col gap-4 shadow-lg max-h-[460px] py-2',
      )}
    >
      <div className="flex justify-between items-center px-4">
        <Typography
          className={sprinkles({ color: 'N-100', py: 'm', fontWeight: 'bold' })}
        >
          CLIPBOARD
        </Typography>
        <IconButton
          onClick={close}
          icon={faClose}
          variant="text"
          color="neutral"
        />
      </div>
      <div className="flex gap-2 px-4">
        <Button color="neutral" onClick={pasteAll} disabled={!!disabledPaste}>
          Paste all
        </Button>
        <Button variant="text" color="neutral" onClick={clear}>
          Clear all
        </Button>
      </div>
      <div className="flex justify-between items-center px-4">
        <Typography>Select to paste</Typography>
        <Button
          variant="filled"
          color="primaryTonal"
          onClick={pasteSelection}
          disabled={!!disabledPaste}
        >
          Paste selection
        </Button>
      </div>
      <div className="overflow-y-auto">
        {data.map((d, index) => (
          <ClipboardPanelItem
            key={d.name}
            data={d}
            index={index}
            deleteItem={deleteItem}
            selected={selection.includes(index)}
            addSelection={addSelection}
            removeSelection={removeSelection}
          />
        ))}
      </div>
    </div>
  );
};
